import React, { useEffect, useMemo, useState } from "react";
import { api, brl } from "@/api";
import { Money, HideMoneyButton } from "@/contexts/HiddenValues";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Power, Trash2, Store, Users, ShoppingCart, KeyRound, Settings,
  CalendarClock, History, Send, LogIn, Edit2, Copy, Check, Wallet, RefreshCw,
  Briefcase, MessageCircle, XCircle, ExternalLink, Globe, Search, Bell, Target, Tag, Filter,
  CreditCard,
} from "lucide-react";
import DiscountDialog from "@/components/master/DiscountDialog";
import PlansAdminDialog from "@/components/master/PlansAdminDialog";
import StripeConfigPanel from "@/pages/StripeConfigPanel";
import { useNavigate } from "react-router-dom";

// ───── constantes ─────
const PLANS = [
  { key: "Go",         label: "Plano Go",         price: 29.90 },
  { key: "Start",      label: "Plano Start",      price: 49.90 },
  { key: "Start Duo",  label: "Plano Start Duo",  price: 79.90 },
  { key: "Start+",     label: "Plano Start+",     price: 69.90 },
  { key: "Pro",        label: "Plano Pro",        price: 99.90 },
  { key: "Pro+",       label: "Plano Pro+",       price: 139.90 },
];
const TRIAL_PRESETS = [2, 7, 14, 30];
const PAID_PRESETS = [1, 3, 6, 12, 18, 24];

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const waLink = (phone, msg) => {
  const d = onlyDigits(phone);
  if (!d) return null;
  const full = d.startsWith("55") ? d : "55" + d;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
};

export default function Master() {
  const { user, impersonate } = useAuth();
  const [stats, setStats] = useState({ tenants_total: 0, tenants_active: 0, users_total: 0, users_active: 0, orders_total: 0 });
  const [metrics, setMetrics] = useState({ monthly_revenue_estimate: 0, active_paid_tenants: 0, active_trial_tenants: 0, disabled_tenants: 0 });
  const [tenants, setTenants] = useState([]);
  const [segments, setSegments] = useState([]);
  const [settings, setSettings] = useState({ whatsapp: "", pix_key: "", pix_holder: "" });

  // diálogos
  const [showCreate, setShowCreate] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [planTarget, setPlanTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [supportTarget, setSupportTarget] = useState(null);
  const [loginInfoTarget, setLoginInfoTarget] = useState(null);
  const [loginInfo, setLoginInfo] = useState(null);
  const [adesaoTarget, setAdesaoTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [discountTarget, setDiscountTarget] = useState(null);
  const [showPlansAdmin, setShowPlansAdmin] = useState(false);

  // Busca + filtros
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState([]); // ['active','trial',...]
  const [planNames, setPlanNames] = useState([]); // nomes vindos de /master/plans
  const [livePlans, setLivePlans] = useState(PLANS); // planos ao vivo com preços atuais (fallback = constante)
  const [showFilters, setShowFilters] = useState(false);

  const nav = useNavigate();

  const load = async (opts = {}) => {
    const q = opts.q ?? search;
    const filters = opts.filters ?? activeFilters;
    const params = {};
    if (q) params.q = q;
    if (filters && filters.length > 0) params.filters = filters.join(",");
    const [s, t, seg, m, st] = await Promise.all([
      api.get("/master/stats", { _cache: false }),
      api.get("/master/tenants", { _cache: false, params }),
      api.get("/master/segments", { _cache: false }),
      api.get("/master/dashboard-metrics", { _cache: false }),
      api.get("/master/settings", { _cache: false }),
    ]);
    setStats(s.data);
    setTenants(t.data);
    setSegments(seg.data || []);
    setMetrics(m.data);
    setSettings(st.data || { whatsapp: "", pix_key: "", pix_holder: "" });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // debounce de busca (350ms)
    const id = setTimeout(() => { load({ q: search, filters: activeFilters }); }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeFilters]);
  useEffect(() => {
    api.get("/master/plans", { _cache: false }).then(r => {
      const list = r.data || [];
      setPlanNames(list.map(p => p.name || p.key));
      // Mapeia pro formato local {key,label,price} — usado nos dialogs.
      if (list.length) {
        setLivePlans(list.map(p => ({ key: p.key, label: p.name || p.key, price: Number(p.price || 0) })));
      }
    }).catch(() => {});
  }, [showPlansAdmin]);

  if (!user || user.role !== "master") {
    return <div className="text-sm text-neutral-500">Acesso restrito.</div>;
  }

  const cards = [
    { label: "Lojas ativas",       value: `${stats.tenants_active} / ${stats.tenants_total}`, icon: Store },
    { label: "Usuários ativos",    value: `${stats.users_active} / ${stats.users_total}`, icon: Users },
    { label: "Pedidos no sistema", value: stats.orders_total, icon: ShoppingCart },
  ];

  return (
    <div className="space-y-8" data-testid="master-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-tiny mb-2">Master</p>
          <h1 className="font-display text-4xl tracking-tight">Lojistas</h1>
          <p className="text-sm text-neutral-500 mt-1">Gerencie todas as lojas do seu SaaS.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={()=>nav("/master/notificacoes")} data-testid="open-notifications-button" title="Notificações para lojistas">
            <Bell size={14} className="mr-1.5"/> Notificações
          </Button>
          <Button variant="outline" onClick={()=>nav("/master/crm")} data-testid="open-master-crm-button" title="CRM (leads e lojistas)">
            <Target size={14} className="mr-1.5"/> CRM
          </Button>
          <Button variant="outline" onClick={()=>setShowPlansAdmin(true)} data-testid="open-plans-admin-button" title="Gerenciar planos e preços">
            <Wallet size={14} className="mr-1.5"/> Planos
          </Button>
          <Button variant="outline" onClick={()=>window.location.href="/master/landing"} data-testid="open-landing-admin-button" title="Configurar a landing page">
            <Globe size={14} className="mr-1.5"/> Landing (admin)
          </Button>
          <Button variant="outline" onClick={()=>window.open("/landingpage", "_blank", "noopener,noreferrer")} data-testid="open-landing-button" title="Abrir landing page">
            <ExternalLink size={14} className="mr-1.5"/> Ver landing
          </Button>
          <Button variant="outline" onClick={()=>setShowSettings(true)} data-testid="open-admin-panel-button">
            <Briefcase size={14} className="mr-1.5"/> Painel do gestor
          </Button>
          <Button variant="outline" onClick={()=>{
            const url = `${window.location.origin}/cadastro`;
            navigator.clipboard.writeText(url);
            toast.success("Link /cadastro copiado");
          }} data-testid="copy-signup-link" title="Copiar link público de cadastro">
            <Copy size={14} className="mr-1.5"/> Copiar link /cadastro
          </Button>
          <Button onClick={()=>setShowCreate(true)} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-tenant-button">
            <Plus size={16} className="mr-1"/> Nova loja
          </Button>
          <HideMoneyButton testId="master-hide-money"/>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {cards.map(c => (
          <Card key={c.label} className="p-6 border-[#E5E5E5] shadow-none">
            <div className="flex items-start justify-between">
              <p className="label-tiny">{c.label}</p>
              <c.icon size={16} className="text-neutral-400"/>
            </div>
            <div className="font-display text-3xl tracking-tight mt-4">{c.value}</div>
          </Card>
        ))}
        <Card className="p-6 border-[#E5E5E5] shadow-none bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-transparent" data-testid="metric-revenue-card">
          <div className="flex items-start justify-between">
            <p className="label-tiny text-emerald-700 dark:text-emerald-300">Faturamento mensal estimado</p>
            <Wallet size={16} className="text-emerald-600"/>
          </div>
          <div className="font-display text-3xl tracking-tight mt-4 text-emerald-700 dark:text-emerald-300"><Money value={metrics.monthly_revenue_estimate}/></div>
          <p className="text-[11px] text-neutral-500 mt-1">{metrics.active_paid_tenants} pago(s) · {metrics.active_trial_tenants} em teste</p>
        </Card>
      </div>

      {/* ── Busca + Filtros (popover harmônico) ─────────────────── */}
      <Card className="p-4 border-[#E5E5E5] shadow-none">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar por loja, dono, e-mail ou telefone…" className="pl-9" data-testid="tenant-search-input"/>
          </div>
          <Button variant={showFilters ? "default" : "outline"} onClick={() => setShowFilters(v => !v)} data-testid="filters-toggle-button" className={showFilters ? "bg-[#0A0A0A] hover:bg-black text-white" : ""}>
            <Filter size={14} className="mr-1.5"/> Filtros {activeFilters.length > 0 && <span className="ml-1.5 text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">{activeFilters.length}</span>}
          </Button>
          {activeFilters.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setActiveFilters([])} data-testid="clear-filters-button" className="text-neutral-500">
              Limpar
            </Button>
          )}
        </div>

        {showFilters && (
          <div className="mt-4 p-4 rounded-md bg-neutral-50 dark:bg-white/5 border border-[#E5E5E5] dark:border-white/10 space-y-3" data-testid="filters-panel">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">Status da loja</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { key: "active",   label: "Assinatura ativa" },
                  { key: "trial",    label: "Em teste" },
                  { key: "expired",  label: "Trial expirado" },
                  { key: "exp_5d",   label: "Vencendo em 5 dias" },
                  { key: "trial_3d", label: "Trial vencendo (3d)" },
                  { key: "no_sub",   label: "Sem assinatura" },
                  { key: "disabled", label: "Desativadas" },
                ].map(f => {
                  const on = activeFilters.includes(f.key);
                  return (
                    <button key={f.key} type="button"
                      onClick={() => setActiveFilters(prev => prev.includes(f.key) ? prev.filter(x => x !== f.key) : [...prev, f.key])}
                      className={`text-xs px-2.5 py-1 rounded-md border transition ${on ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-neutral-700 border-[#E5E5E5] hover:bg-neutral-50"}`}
                      data-testid={`filter-${f.key}`}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {planNames.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">Por plano</p>
                <div className="flex flex-wrap gap-1.5">
                  {planNames.map(name => {
                    const key = `plan:${name}`;
                    const on = activeFilters.includes(key);
                    return (
                      <button key={name} type="button"
                        onClick={() => setActiveFilters(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key])}
                        className={`text-xs px-2.5 py-1 rounded-md border transition ${on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-neutral-700 border-[#E5E5E5] hover:bg-neutral-50"}`}
                        data-testid={`filter-plan-${name}`}>
                        {name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 text-[11px] text-neutral-500">
          {tenants.length} loja(s) encontrada(s){activeFilters.length > 0 ? ` · ${activeFilters.length} filtro(s) ativo(s)` : ""}
        </div>
      </Card>

      <div className="space-y-4">
        {tenants.map(t => (
          <TenantRow
            key={t.id}
            tenant={t}
            segments={segments}
            settings={settings}
            plans={livePlans}
            onRenew={()=>setRenewTarget(t)}
            onHistory={async () => {
              const r = await api.get(`/master/tenants/${t.id}/subscription-history`, { _cache: false });
              setHistoryRows(r.data || []);
              setHistoryTarget(t);
            }}
            onPlans={()=>setPlanTarget(t)}
            onDiscount={()=>setDiscountTarget(t)}
            onAdesao={()=>setAdesaoTarget(t)}
            onCancel={()=>setCancelTarget(t)}
            onReconcile={async () => {
              if (!window.confirm(`Forçar reconciliação com o Stripe para "${t.name}"?\n\nVai buscar sessões pagas, subscriptions ativas e invoices pagas no customer_id, aplicando renovação se encontrar. Use quando o pagamento foi aprovado mas o acesso não voltou.`)) return;
              try {
                const r = await api.post(`/master/tenants/${t.id}/force-reconcile`);
                const notes = r.data?.notes || [];
                if (r.data?.reconciled) {
                  toast.success(`Reconciliado! ${notes.join(" · ")}`);
                } else {
                  // Detecta erro de autenticação do Stripe (chave expirada/inválida)
                  const authErr = notes.find(n => /Expired API Key|Invalid API Key|No API key|api_key_expired/i.test(n));
                  if (authErr) {
                    toast.error("Chave Stripe expirada/inválida. Atualize a Secret Key em Configurações → Stripe (ícone de engrenagem no topo do painel Master).", { duration: 8000 });
                  } else if (notes.length) {
                    toast.message(`Nada pra reconciliar. ${notes.join(" · ")}`, { duration: 6000 });
                  } else {
                    toast.message("Nada pra reconciliar — nenhum pagamento pendente encontrado para este cliente no Stripe.");
                  }
                }
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Erro ao reconciliar", { duration: 8000 });
              }
            }}
            onEdit={()=>setEditTarget(t)}
            onSupport={()=>setSupportTarget(t)}
            onSendLogin={async () => {
              const r = await api.get(`/master/tenants/${t.id}/login-info`, { _cache: false });
              setLoginInfo(r.data);
              setLoginInfoTarget(t);
            }}
            onResetPassword={async () => {
              if (!window.confirm(`Definir senha "adminadmin" para o dono da loja "${t.name}"?`)) return;
              await api.post(`/master/tenants/${t.id}/set-default-password`);
              toast.success("Senha definida para adminadmin");
            }}
            onToggle={async () => {
              await api.put(`/master/tenants/${t.id}`, { disabled: !t.disabled });
              load();
            }}
            onRemove={async () => {
              if (!window.confirm(`Excluir a loja "${t.name}" e TODOS os dados? Ação irreversível.`)) return;
              await api.delete(`/master/tenants/${t.id}`);
              toast.success("Loja excluída");
              load();
            }}
          />
        ))}
        {tenants.length === 0 && <div className="text-center py-12 text-sm text-neutral-500">Nenhuma loja cadastrada ainda.</div>}
      </div>

      <CreateTenantDialog
        open={showCreate}
        onClose={()=>setShowCreate(false)}
        plans={livePlans}
        segments={segments}
        onSegmentsChange={async () => {
          const r = await api.get("/master/segments", { _cache: false });
          setSegments(r.data || []);
        }}
        onCreated={() => { setShowCreate(false); load(); }}
      />

      <AdminSettingsDialog
        open={showSettings}
        onClose={()=>setShowSettings(false)}
        initial={settings}
        metrics={metrics}
        onSaved={(s) => { setSettings(s); toast.success("Configurações salvas"); }}
      />

      <RenewDialog
        target={renewTarget}
        onClose={()=>setRenewTarget(null)}
        onDone={() => { setRenewTarget(null); load(); }}
      />

      <HistoryDialog
        target={historyTarget}
        rows={historyRows}
        onClose={()=>setHistoryTarget(null)}
      />

      <PlansDialog
        target={planTarget}
        plans={livePlans}
        onClose={()=>setPlanTarget(null)}
        onUpdate={async (planKey) => {
          await api.put(`/master/tenants/${planTarget.id}`, { plan: planKey });
          toast.success("Plano atualizado");
          setPlanTarget(null);
          load();
        }}
      />

      <EditTenantDialog
        target={editTarget}
        segments={segments}
        onClose={()=>setEditTarget(null)}
        onSaved={() => { setEditTarget(null); load(); }}
      />

      <SupportDialog
        target={supportTarget}
        onClose={()=>setSupportTarget(null)}
        onConfirm={async (password) => {
          try {
            await impersonate(supportTarget.id, password);
            window.location.href = "/";
          } catch (e) {
            toast.error(e.response?.data?.detail || "Senha incorreta");
          }
        }}
      />

      <LoginInfoDialog
        target={loginInfoTarget}
        info={loginInfo}
        whatsapp={loginInfoTarget?.owner_phone}
        onClose={()=>{ setLoginInfoTarget(null); setLoginInfo(null); }}
      />

      <AdesaoDialog
        target={adesaoTarget}
        plans={livePlans}
        onClose={()=>setAdesaoTarget(null)}
      />

      <CancelSubscriptionDialog
        target={cancelTarget}
        onClose={()=>setCancelTarget(null)}
        onDone={() => { setCancelTarget(null); load(); }}
      />

      <DiscountDialog
        target={discountTarget}
        onClose={()=>setDiscountTarget(null)}
        onSaved={()=>{ setDiscountTarget(null); load(); }}
      />

      <PlansAdminDialog
        open={showPlansAdmin}
        onClose={()=>setShowPlansAdmin(false)}
        onSaved={()=>load()}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tenant Row
// ─────────────────────────────────────────────────────────────────────────────
function daysLeftColor(daysLeft, expired) {
  if (expired) return "text-red-600";
  if (daysLeft == null) return "text-neutral-500";
  if (daysLeft <= 3) return "text-red-600";
  if (daysLeft <= 7) return "text-orange-600";
  return "text-emerald-700 dark:text-emerald-400";
}

function TenantRow({ tenant: t, segments, settings, plans, onRenew, onHistory, onPlans, onDiscount, onAdesao, onCancel, onReconcile, onEdit, onSupport, onSendLogin, onResetPassword, onToggle, onRemove }) {
  const PLAN_LIST = (plans && plans.length) ? plans : PLANS;
  const status = t.subscription_status || {};
  const daysLeft = status.days_left;
  const expired = status.expired;
  const discount = status.discount || t.discount;
  const hasDiscount = !!(discount && discount.kind);
  const basePrice = status.plan_price;
  const finalPrice = status.final_price ?? basePrice;
  const colorClass = daysLeftColor(daysLeft, expired);
  const dayLabel = expired
    ? (daysLeft != null ? `Expirado há ${Math.abs(daysLeft)} dia${Math.abs(daysLeft) === 1 ? "" : "s"}` : "Expirado")
    : (daysLeft == null ? "Sem assinatura" : `${daysLeft} dia${daysLeft === 1 ? "" : "s"} restantes`);
  const segmentNames = (t.segment_ids || [])
    .map(id => segments.find(s => s.id === id)?.name)
    .filter(Boolean);

  // Monta link de pagamento usando o BACKEND_URL atual (preview ou prod).
  // Frontend e backend compartilham o mesmo domínio neste app.
  const buildPayLink = (amount, desc) => {
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    const params = new URLSearchParams({ valor: String(amount), desc: desc || "" });
    return `${base}/pagar/master?${params.toString()}`;
  };

  // Mensagens de cobrança WhatsApp
  const sendCharge = () => {
    const phone = t.owner_phone;
    if (!phone) { toast.error("Lojista sem telefone cadastrado"); return; }
    // Preço vem do backend (status.plan_price / final_price) — fonte da verdade.
    // Isso garante que edições feitas em "Planos" sejam refletidas sem cache stale.
    const plan = (PLAN_LIST.find(p => p.key === t.plan) || PLAN_LIST[1] || PLAN_LIST[0]);
    const price = (finalPrice != null ? finalPrice : (basePrice != null ? basePrice : plan.price));
    const planLabel = status.plan_label || plan.label;
    const payLink = buildPayLink(price, `Renovação ${planLabel} — ${t.name}`);
    let msg;
    if (expired && daysLeft != null && daysLeft >= -2) {
      const overdueDays = Math.abs(daysLeft);
      const overdueLabel = overdueDays === 1 ? "1 dia" : `${overdueDays} dias`;
      msg = `Olá! Tudo bem?\nSua assinatura está ${overdueLabel} vencida, e no terceiro dia sua conta de acesso terá o acesso bloqueado automaticamente, para evitar bloqueio, segue o link de pagamento.\nAo fazer o pagamento nos mande o comprovante aqui neste WhatsApp.\nObrigado, tenha um excelente dia!\nBoas vendas 💰\n\n${planLabel} — ${brl(price)}\n${payLink}`;
    } else {
      const remainLabel = daysLeft != null ? (daysLeft === 1 ? "1 dia" : `${daysLeft} dias`) : "alguns dias";
      msg = `Olá! Tudo bem?\nSegue o link para pagamento da sua renovação do sistema Gestão Expert.\nSua assinatura expira em ${remainLabel}.\nAo fazer o pagamento nos mande o comprovante aqui neste WhatsApp.\nObrigado, tenha um excelente dia!\nBoas vendas 💰\n\n${planLabel} — ${brl(price)}\n${payLink}`;
    }
    const url = waLink(phone, msg);
    if (!url) { toast.error("Telefone inválido"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Envia checkout Stripe direto do botão "Cobrar" — respeita plano+desconto do tenant.
  const sendChargeStripe = async (mode /* "card" | "pix" */, copyOnly = false) => {
    const phone = t.owner_phone;
    if (!copyOnly && !phone) { toast.error("Lojista sem telefone cadastrado"); return; }
    // Pre-abre janela SÍNCRONA (SEM noopener — senão retorna null e a mitigação não vale).
    let waWindow = null;
    if (!copyOnly) waWindow = window.open("about:blank", "_blank");
    try {
      const r = await api.post(`/master/tenants/${t.id}/stripe-checkout`, {
        tenant_id: t.id, months: 1, payment_mode: mode,
        origin_url: window.location.origin,
        source: copyOnly ? "admin_direct" : "admin_whatsapp",
      });
      const link = r.data?.checkout_url;
      if (!link) throw new Error("Sem link");
      if (copyOnly) {
        await navigator.clipboard.writeText(link);
        toast.success("Link do checkout copiado");
        return;
      }
      const modeLabel = mode === "card" ? "no cartão (assinatura mensal recorrente)" : "via PIX";
      const msg2 = `Olá! Tudo bem?\nSegue o link para pagamento da sua renovação do sistema Gestão Expert ${modeLabel}:\n\n${link}\n\nPagamento seguro processado pelo Stripe.`;
      const url2 = waLink(phone, msg2);
      if (waWindow && !waWindow.closed) {
        try { waWindow.opener = null; } catch { /* ignore */ }
        waWindow.location.href = url2;
      } else {
        window.open(url2, "_blank", "noopener,noreferrer");
      }
      toast.success("Link enviado no WhatsApp");
    } catch (e) {
      if (waWindow && !waWindow.closed) waWindow.close();
      toast.error(e?.response?.data?.detail || "Erro ao gerar checkout Stripe.");
    }
  };

  return (
    <Card
      className={`group relative overflow-hidden border border-[#E5E5E5] dark:border-white/10 dark:bg-gradient-to-br dark:from-[#0F1626] dark:to-[#0A0F19] shadow-sm hover:shadow-md transition-shadow duration-300 ${t.disabled ? "opacity-70" : ""}`}
      data-testid={`tenant-${t.id}`}
    >
      {/* Brilho discreto no topo no dark mode */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent dark:via-white/15 pointer-events-none"/>

      <div className="p-5 sm:p-6">
        {/* ── HEADER ────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-xl tracking-tight" data-testid={`tenant-name-${t.id}`}>{t.name}</h3>
              {t.disabled && <Pill tone="amber">Desativada</Pill>}
              {status.subscription_type === "trial" && <Pill tone="blue">Teste</Pill>}
              {status.subscription_type === "paid" && <Pill tone="emerald">Assinatura</Pill>}
            </div>
            <div className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono mt-1">/catalogo/{t.slug}</div>
          </div>

          {/* ── STATUS BADGE (canto direito) ───────────────────────── */}
          <div className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border ${expired
              ? "bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-900/40"
              : daysLeft != null && daysLeft <= 3
                ? "bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-900/40"
                : daysLeft != null && daysLeft <= 7
                  ? "bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-900/40"
                  : daysLeft == null
                    ? "bg-neutral-50 border-neutral-200 dark:bg-white/5 dark:border-white/10"
                    : "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-900/40"}`}>
            <CalendarClock size={14} className={colorClass}/>
            <span className={`text-xs font-medium ${colorClass}`} data-testid={`days-left-${t.id}`}>{dayLabel}</span>
          </div>
        </div>

        {/* ── META INFO ─────────────────────────────────────────── */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-medium">Plano</p>
            <p className="text-sm mt-0.5 truncate font-medium text-neutral-800 dark:text-neutral-200">{status.plan_label || "—"}</p>
            {basePrice != null && (
              hasDiscount ? (
                <p className="text-[11px] mt-0.5 truncate">
                  <span className="line-through text-neutral-400 mr-1">{brl(basePrice)}</span>
                  <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{brl(finalPrice)}</span>
                </p>
              ) : (
                <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">{brl(basePrice)}</p>
              )
            )}
            {hasDiscount && (
              <p className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" title={discount.note || ""}>
                <Tag size={9}/> {discount.kind === "percent" ? `${discount.value}%` : `R$ ${discount.value}`}
                {discount.duration === "months" ? ` · ${discount.months_remaining ?? discount.original_months}m` : " · Permanente"}
              </p>
            )}
          </div>
          <Meta label="Dono" value={t.owner?.email || "—"} accent={t.owner_phone || null} mono/>
          <Meta label="Usuários" value={`${t.users_count}`}/>
          <Meta label="Pedidos" value={`${t.orders_count}`}/>
        </div>

        {/* Segmentos */}
        {segmentNames.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {segmentNames.map(n => (
              <span key={n} className="text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded-md bg-neutral-100 dark:bg-white/5 text-neutral-600 dark:text-neutral-300 border border-[#E5E5E5] dark:border-white/10">
                {n}
              </span>
            ))}
          </div>
        )}

        {/* ── AÇÕES — agrupadas por categoria com separadores ──── */}
        <div className="mt-5 pt-4 border-t border-[#E5E5E5] dark:border-white/10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Lado esquerdo: ações de assinatura, cobrança e acesso */}
            <div className="flex flex-wrap items-center gap-1.5">
              <ActionBtn onClick={onRenew} icon={RefreshCw} testid={`renew-${t.id}`} title="Renovar / alterar assinatura">Renovar</ActionBtn>
              <ActionBtn onClick={onHistory} icon={History} testid={`history-${t.id}`} title="Histórico">Histórico</ActionBtn>
              <ActionBtn onClick={onPlans} icon={Wallet} testid={`plans-${t.id}`}>Planos</ActionBtn>
              <ActionBtn onClick={onDiscount} icon={Tag} tone={hasDiscount ? "emerald" : undefined} testid={`discount-${t.id}`} title="Aplicar/editar desconto">{hasDiscount ? "Desconto ativo" : "Desconto"}</ActionBtn>

              <Divider/>

              <ActionBtn onClick={onAdesao} icon={Briefcase} tone="amber" testid={`adesao-${t.id}`} title="Cobrar adesão (1ª mensalidade ou pacote inicial)">Cobrar adesão</ActionBtn>
              <ActionBtn onClick={sendCharge} icon={Send} tone="emerald" testid={`charge-${t.id}`} title="Enviar cobrança no WhatsApp (PIX manual)">Cobrar</ActionBtn>
              <ActionBtn onClick={()=>sendChargeStripe("card")} icon={CreditCard} tone="indigo" testid={`charge-stripe-card-${t.id}`} title="Enviar cobrança via Stripe (cartão recorrente)">Cobrar (Cartão)</ActionBtn>
              <ActionBtn onClick={()=>sendChargeStripe("card", true)} icon={Copy} tone="neutral" testid={`charge-stripe-copy-${t.id}`} title="Copiar link do checkout Stripe">Copiar link</ActionBtn>
              <ActionBtn onClick={onReconcile} icon={RefreshCw} tone="indigo" testid={`reconcile-${t.id}`} title="Forçar reconciliação com Stripe (destravar cliente que pagou mas acesso não voltou)">Reconciliar Stripe</ActionBtn>

              <Divider/>

              <ActionBtn onClick={onSendLogin} icon={KeyRound} testid={`send-login-${t.id}`} title="Enviar dados de login">Enviar login</ActionBtn>
              <ActionBtn onClick={onEdit} icon={Edit2} testid={`edit-${t.id}`}>Editar</ActionBtn>
              <ActionBtn onClick={onResetPassword} icon={KeyRound} tone="amber-soft" testid={`reset-pwd-${t.id}`} title="Definir senha 'adminadmin'">Senha padrão</ActionBtn>
              <ActionBtn onClick={onSupport} icon={LogIn} testid={`support-${t.id}`}>Prestar suporte</ActionBtn>
            </div>

            {/* Lado direito: ações destrutivas/estado (mantidas juntas) */}
            <div className="flex flex-wrap items-center gap-1.5">
              {status.subscription_type && (
                <ActionBtn onClick={onCancel} icon={XCircle} tone="red" testid={`cancel-sub-${t.id}`} title="Cancelar assinatura/teste">Cancelar</ActionBtn>
              )}
              <ActionBtn onClick={onToggle} icon={Power} testid={`toggle-${t.id}`}>{t.disabled ? "Ativar" : "Desativar"}</ActionBtn>
              <button
                onClick={onRemove}
                className="h-8 w-8 rounded-md flex items-center justify-center text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                data-testid={`remove-${t.id}`}
                title="Excluir loja"
              >
                <Trash2 size={14}/>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── Sub-componentes do TenantRow ───────────────────────────────────────────
function Pill({ children, tone }) {
  const tones = {
    amber: "bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300",
    blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
    emerald: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-md ${tones[tone] || tones.amber}`}>{children}</span>;
}

function Meta({ label, value, accent, mono }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-medium">{label}</p>
      <p className={`text-sm mt-0.5 truncate ${mono ? "font-mono text-xs" : "font-medium"} text-neutral-800 dark:text-neutral-200`}>{value}</p>
      {accent && <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">{accent}</p>}
    </div>
  );
}

function Divider() {
  return <span className="h-5 w-px bg-[#E5E5E5] dark:bg-white/10 mx-1 hidden sm:inline-block" aria-hidden="true"/>;
}

function ActionBtn({ onClick, icon: Icon, tone, testid, title, children }) {
  const tones = {
    emerald: "text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border-emerald-200/60 dark:border-emerald-900/30",
    amber: "text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 border-amber-200/60 dark:border-amber-900/30",
    "amber-soft": "text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/15",
    red: "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200/60 dark:border-red-900/30",
    indigo: "text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-indigo-200/60 dark:border-indigo-900/30",
    neutral: "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/5",
  };
  const toneClass = tones[tone] || "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/5";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-[#E5E5E5] dark:border-white/10 bg-white dark:bg-transparent text-xs font-medium transition-all duration-150 hover:shadow-sm active:scale-[0.98] ${toneClass}`}
    >
      <Icon size={12}/> {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create Tenant Dialog
// ─────────────────────────────────────────────────────────────────────────────
const blankForm = {
  name: "", slug: "",
  owner_email: "", owner_password: "",
  owner_name: "", owner_cpf: "", owner_address: "", owner_phone: "", owner_instagram: "",
  segment_ids: [],
  plan: "Start",
  subscription_type: null,
  period_value: null,
  period_unit: null,
  subscription_note: "",
  custom_input: "",
};

function CreateTenantDialog({ open, onClose, segments, plans, onSegmentsChange, onCreated }) {
  const PLAN_LIST = (plans && plans.length) ? plans : PLANS;
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [newSegmentName, setNewSegmentName] = useState("");
  const [creatingSegment, setCreatingSegment] = useState(false);

  useEffect(() => { if (open) setForm(blankForm); }, [open]);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleSegment = (id) => {
    setForm(prev => ({
      ...prev,
      segment_ids: prev.segment_ids.includes(id)
        ? prev.segment_ids.filter(s => s !== id)
        : [...prev.segment_ids, id],
    }));
  };

  const createSegment = async () => {
    if (!newSegmentName.trim()) return;
    setCreatingSegment(true);
    try {
      const r = await api.post("/master/segments", { name: newSegmentName.trim() });
      setNewSegmentName("");
      await onSegmentsChange();
      upd("segment_ids", [...form.segment_ids, r.data.id]);
      toast.success("Segmento criado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setCreatingSegment(false); }
  };

  const submit = async () => {
    if (!form.name || !form.slug || !form.owner_email || !form.owner_password) {
      return toast.error("Preencha nome, slug, email e senha");
    }
    if (!form.owner_phone) return toast.error("Número de celular é obrigatório");
    setBusy(true);
    try {
      const payload = {
        ...form,
        subscription_period_value: form.period_value,
        subscription_period_unit: form.period_unit,
      };
      delete payload.period_value; delete payload.period_unit; delete payload.custom_input;
      await api.post("/master/tenants", payload);
      toast.success("Loja criada!");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="create-tenant-dialog">
        <DialogHeader><DialogTitle>Nova loja</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <section className="space-y-3">
            <p className="label-tiny">Loja</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Nome da loja</Label><Input value={form.name} onChange={e=>upd("name", e.target.value)} placeholder="Ex: Pizzaria do João" data-testid="tenant-name-input"/></div>
              <div>
                <Label>Slug (URL do catálogo)</Label>
                <Input value={form.slug} onChange={e=>upd("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="ex: pizzariadojoao" data-testid="tenant-slug-input"/>
              </div>
            </div>
          </section>

          <section className="space-y-2">
            <p className="label-tiny">Segmento</p>
            <div className="flex flex-wrap gap-2">
              {segments.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={()=>toggleSegment(s.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${form.segment_ids.includes(s.id) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-neutral-700 border-[#E5E5E5] hover:border-neutral-400"} dark:bg-transparent dark:text-neutral-300 dark:border-white/10 ${form.segment_ids.includes(s.id) ? "dark:bg-white dark:text-[#0A0A0A]" : ""}`}
                  data-testid={`segment-toggle-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
              {segments.length === 0 && <span className="text-xs text-neutral-400">Nenhum segmento cadastrado ainda.</span>}
            </div>
            <div className="flex gap-2 items-center">
              <Input value={newSegmentName} onChange={e=>setNewSegmentName(e.target.value)} placeholder="Criar novo segmento…" className="h-8 text-sm" data-testid="new-segment-input"/>
              <Button size="sm" variant="outline" onClick={createSegment} disabled={creatingSegment || !newSegmentName.trim()} data-testid="create-segment-button"><Plus size={12} className="mr-1"/> Criar</Button>
            </div>
          </section>

          <section className="space-y-3">
            <p className="label-tiny">Dados do lojista</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Nome do lojista <span className="text-neutral-400">(opcional)</span></Label><Input value={form.owner_name} onChange={e=>upd("owner_name", e.target.value)} data-testid="owner-name-input"/></div>
              <div><Label>CPF <span className="text-neutral-400">(opcional)</span></Label><Input value={form.owner_cpf} onChange={e=>upd("owner_cpf", e.target.value)} data-testid="owner-cpf-input"/></div>
              <div className="md:col-span-2"><Label>Endereço <span className="text-neutral-400">(opcional)</span></Label><Input value={form.owner_address} onChange={e=>upd("owner_address", e.target.value)} data-testid="owner-address-input"/></div>
              <div><Label>Celular <span className="text-red-500">*</span></Label><Input value={form.owner_phone} onChange={e=>upd("owner_phone", e.target.value)} placeholder="+5511999999999" data-testid="owner-phone-input"/></div>
              <div><Label>Instagram <span className="text-neutral-400">(opcional)</span></Label><Input value={form.owner_instagram} onChange={e=>upd("owner_instagram", e.target.value)} placeholder="@usuario" data-testid="owner-instagram-input"/></div>
            </div>
          </section>

          <section className="space-y-3">
            <p className="label-tiny">Login</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.owner_email} onChange={e=>upd("owner_email", e.target.value)} data-testid="tenant-email-input"/></div>
              <div>
                <Label>Senha inicial</Label>
                <div className="flex gap-2">
                  <Input value={form.owner_password} onChange={e=>upd("owner_password", e.target.value)} data-testid="tenant-password-input"/>
                  <Button size="sm" variant="outline" onClick={()=>upd("owner_password", "adminadmin")} title="Preencher com adminadmin" data-testid="default-password-button"><KeyRound size={12} className="mr-1"/> Senha padrão</Button>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <p className="label-tiny">Plano e período</p>
            <div>
              <Label>Plano</Label>
              <Select value={form.plan} onValueChange={(v)=>upd("plan", v)}>
                <SelectTrigger data-testid="plan-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {PLAN_LIST.map(p => <SelectItem key={p.key} value={p.key}>{p.label} — {brl(p.price)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <PeriodPicker
              value={{ type: form.subscription_type, value: form.period_value, unit: form.period_unit, custom: form.custom_input }}
              onChange={(v)=>setForm(prev => ({ ...prev, subscription_type: v.type, period_value: v.value, period_unit: v.unit, custom_input: v.custom }))}
            />
            <div>
              <Label>Observação <span className="text-neutral-400">(opcional)</span></Label>
              <Textarea value={form.subscription_note} onChange={e=>upd("subscription_note", e.target.value)} rows={2} data-testid="subscription-note-input"/>
            </div>
          </section>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="create-tenant-button">{busy ? "Criando…" : "Criar loja"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Period Picker (compartilhado entre Create e Renew)
// ─────────────────────────────────────────────────────────────────────────────
function PeriodPicker({ value, onChange }) {
  const setType = (type) => {
    onChange({ type, value: null, unit: type === "trial" ? "days" : "months", custom: "" });
  };
  const setPreset = (n) => {
    onChange({ ...value, value: n, custom: "" });
  };
  const setCustom = (c) => {
    onChange({ ...value, value: c ? Number(c) : null, custom: c });
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={()=>setType("trial")}
          className={`text-sm py-2 rounded-md border transition ${value.type === "trial" ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-[#E5E5E5] hover:border-blue-400 dark:bg-transparent dark:border-white/10"}`}
          data-testid="period-type-trial"
        >
          Teste (dias)
        </button>
        <button
          type="button"
          onClick={()=>setType("paid")}
          className={`text-sm py-2 rounded-md border transition ${value.type === "paid" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-[#E5E5E5] hover:border-emerald-400 dark:bg-transparent dark:border-white/10"}`}
          data-testid="period-type-paid"
        >
          Assinatura (meses)
        </button>
      </div>
      {value.type && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {(value.type === "trial" ? TRIAL_PRESETS : PAID_PRESETS).map(n => (
              <button
                key={n}
                type="button"
                onClick={()=>setPreset(n)}
                className={`text-xs px-3 py-1.5 rounded border transition ${value.value === n && !value.custom ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10"}`}
                data-testid={`period-preset-${n}`}
              >
                {n} {value.type === "trial" ? (n === 1 ? "dia" : "dias") : (n === 1 ? "mês" : "meses")}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-neutral-500">Personalizado:</span>
            <Input
              type="number"
              min="1"
              value={value.custom || ""}
              onChange={e=>setCustom(e.target.value)}
              placeholder={value.type === "trial" ? "dias" : "meses"}
              className="h-8 text-sm w-28"
              data-testid="period-custom-input"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Renew Dialog
// ─────────────────────────────────────────────────────────────────────────────
function RenewDialog({ target, onClose, onDone }) {
  const [mode, setMode] = useState("period"); // "period" ou "manual"
  const [picker, setPicker] = useState({ type: "paid", value: null, unit: "months", custom: "" });
  const [manualDate, setManualDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setMode("period");
      setPicker({ type: target.subscription_type === "trial" ? "trial" : "paid",
                  value: null,
                  unit: target.subscription_type === "trial" ? "days" : "months",
                  custom: "" });
      // Pré-preenche a data manual com a atual expiração (facilita ajustes finos)
      if (target.subscription_expires_at) {
        const d = new Date(target.subscription_expires_at);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth()+1).padStart(2,"0");
        const dd = String(d.getDate()).padStart(2,"0");
        setManualDate(`${yyyy}-${mm}-${dd}`);
      } else {
        setManualDate("");
      }
      setNote("");
    }
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "manual") {
        if (!manualDate) { toast.error("Escolha a data"); setBusy(false); return; }
        // Converte DD/MM/AAAA ou AAAA-MM-DD em ISO (meio-dia UTC pra evitar fuso)
        const iso = new Date(`${manualDate}T12:00:00Z`).toISOString();
        await api.post(`/master/tenants/${target.id}/admin-fix-expiration`, {
          expires_at: iso,
          note: note || `Data definida manualmente pelo Master`,
        });
        toast.success("Data de expiração atualizada");
      } else {
        if (!picker.type || !picker.value || picker.value <= 0) { toast.error("Escolha o período"); setBusy(false); return; }
        await api.post(`/master/tenants/${target.id}/renew`, {
          type: picker.type,
          period_value: picker.value,
          period_unit: picker.unit,
          note,
        });
        toast.success("Assinatura renovada");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="renew-dialog">
        <DialogHeader><DialogTitle>Renovar / Alterar assinatura</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-neutral-600 dark:text-neutral-400">
            Loja: <span className="font-medium">{target.name}</span>
            {target.subscription_expires_at && (
              <div className="text-xs text-neutral-500 mt-0.5">Atual expira em: {new Date(target.subscription_expires_at).toLocaleString("pt-BR")}</div>
            )}
          </div>
          {/* Toggle entre adicionar período vs definir data manual */}
          <div className="flex gap-2 p-1 bg-neutral-100 dark:bg-neutral-900 rounded-md">
            <button
              type="button"
              onClick={()=>setMode("period")}
              className={`flex-1 py-1.5 text-xs rounded ${mode==="period" ? "bg-white dark:bg-neutral-700 shadow font-medium" : "text-neutral-500"}`}
              data-testid="renew-mode-period"
            >Adicionar período</button>
            <button
              type="button"
              onClick={()=>setMode("manual")}
              className={`flex-1 py-1.5 text-xs rounded ${mode==="manual" ? "bg-white dark:bg-neutral-700 shadow font-medium" : "text-neutral-500"}`}
              data-testid="renew-mode-manual"
            >Definir data manual</button>
          </div>
          {mode === "period" ? (
            <PeriodPicker value={picker} onChange={setPicker}/>
          ) : (
            <div>
              <Label>Nova data de expiração</Label>
              <input
                type="date"
                value={manualDate}
                onChange={e=>setManualDate(e.target.value)}
                className="w-full border border-neutral-300 dark:border-neutral-700 rounded px-3 py-2 text-sm bg-white dark:bg-neutral-800"
                data-testid="renew-manual-date-input"
              />
              <p className="text-[11px] text-neutral-500 mt-1">
                Define a data de expiração <strong>exata</strong>. Use para corrigir casos onde o reconcile aplicou dias a mais ou a menos.
              </p>
            </div>
          )}
          <div>
            <Label>Observação <span className="text-neutral-400">(opcional)</span></Label>
            <Textarea value={note} onChange={e=>setNote(e.target.value)} rows={2} data-testid="renew-note-input"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="confirm-renew-button">{busy ? "Salvando…" : (mode==="manual" ? "Salvar data" : "Renovar")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// History Dialog
// ─────────────────────────────────────────────────────────────────────────────
function HistoryDialog({ target, rows, onClose }) {
  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader><DialogTitle>Histórico — {target.name}</DialogTitle></DialogHeader>
        <div className="divide-y divide-[#E5E5E5] dark:divide-white/10 border border-[#E5E5E5] dark:border-white/10 rounded-md">
          {rows.length === 0 && <div className="p-6 text-center text-xs text-neutral-500">Sem registros ainda.</div>}
          {rows.map((r, i) => {
            const isCancel = r.action === "cancel";
            const isTrialCancel = r.action === "trial_cancelled";
            const isCreate = r.action === "create";
            return (
            <div key={i} className="p-3 text-sm" data-testid={`history-row-${i}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  {isCancel ? (
                    <>
                      <span className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800">Cancelado</span>
                      <span className="font-medium">{r.type === "trial" ? "Teste" : "Assinatura"} cancelada</span>
                    </>
                  ) : isTrialCancel ? (
                    <>
                      <span className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-800">Teste finalizado</span>
                      <span className="font-medium">Teste cancelado automaticamente</span>
                    </>
                  ) : (
                    <>
                      <span className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded ${r.type === "trial" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>{r.type === "trial" ? "Teste" : "Assinatura"}</span>
                      <span className="font-medium">+{r.period_value} {r.period_unit === "days" ? (r.period_value === 1 ? "dia" : "dias") : (r.period_value === 1 ? "mês" : "meses")}</span>
                      {isCreate && <span className="text-[10px] text-neutral-500 italic">criação</span>}
                    </>
                  )}
                </div>
                <span className="text-xs text-neutral-500">{new Date(r.at).toLocaleString("pt-BR")}</span>
              </div>
              {r.expires_at && !isCancel && (
                <div className="text-xs text-neutral-500 mt-1">
                  Expira em: {new Date(r.expires_at).toLocaleString("pt-BR")}
                  {r.previous_expires_at && <span className="ml-2 text-neutral-400">(antes: {new Date(r.previous_expires_at).toLocaleString("pt-BR")})</span>}
                </div>
              )}
              {(isCancel || isTrialCancel) && r.previous_expires_at && (
                <div className="text-xs text-neutral-500 mt-1">
                  Expiração que seria: {new Date(r.previous_expires_at).toLocaleString("pt-BR")}
                </div>
              )}
              {r.note && <div className="text-xs mt-1.5 bg-amber-50 border border-amber-100 dark:bg-amber-900/10 dark:border-amber-900/30 rounded px-2 py-1"><span className="font-semibold">Obs.:</span> {r.note}</div>}
              {r.by && <div className="text-[10px] text-neutral-400 mt-1">por {r.by}</div>}
            </div>
          );})}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Plans Dialog
// ─────────────────────────────────────────────────────────────────────────────
function PlansDialog({ target, plans, onClose, onUpdate }) {
  if (!target) return null;
  const PLAN_LIST = (plans && plans.length) ? plans : PLANS;
  return (
    <Dialog open={!!target} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="plans-dialog">
        <DialogHeader><DialogTitle>Plano da loja</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500">Selecione o plano de {target.name}.</p>
        <div className="grid grid-cols-1 gap-2 mt-3">
          {PLAN_LIST.map(p => (
            <button
              key={p.key}
              type="button"
              onClick={()=>onUpdate(p.key)}
              className={`flex items-center justify-between p-3 rounded-md border text-left transition ${target.plan === p.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10"}`}
              data-testid={`plan-option-${p.key}`}
            >
              <span className="font-medium">{p.label}</span>
              <span className="text-sm">{brl(p.price)}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Edit Tenant Dialog
// ─────────────────────────────────────────────────────────────────────────────
function EditTenantDialog({ target, segments, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setForm({
        name: target.name || "",
        slug: target.slug || "",
        owner_name: target.owner_name || "",
        owner_cpf: target.owner_cpf || "",
        owner_address: target.owner_address || "",
        owner_phone: target.owner_phone || "",
        owner_instagram: target.owner_instagram || "",
        segment_ids: [...(target.segment_ids || [])],
      });
    }
  }, [target]);

  if (!target || !form) return null;

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleSegment = (id) => {
    setForm(prev => ({
      ...prev,
      segment_ids: prev.segment_ids.includes(id)
        ? prev.segment_ids.filter(s => s !== id)
        : [...prev.segment_ids, id],
    }));
  };

  const submit = async () => {
    if (!form.owner_phone) return toast.error("Celular é obrigatório");
    setBusy(true);
    try {
      await api.put(`/master/tenants/${target.id}`, form);
      toast.success("Loja atualizada");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="edit-tenant-dialog">
        <DialogHeader><DialogTitle>Editar loja</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input value={form.name} onChange={e=>upd("name", e.target.value)} data-testid="edit-name"/></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={e=>upd("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid="edit-slug"/></div>
            <div><Label>Nome do lojista</Label><Input value={form.owner_name} onChange={e=>upd("owner_name", e.target.value)}/></div>
            <div><Label>CPF</Label><Input value={form.owner_cpf} onChange={e=>upd("owner_cpf", e.target.value)}/></div>
            <div className="md:col-span-2"><Label>Endereço</Label><Input value={form.owner_address} onChange={e=>upd("owner_address", e.target.value)}/></div>
            <div><Label>Celular *</Label><Input value={form.owner_phone} onChange={e=>upd("owner_phone", e.target.value)} data-testid="edit-phone"/></div>
            <div><Label>Instagram</Label><Input value={form.owner_instagram} onChange={e=>upd("owner_instagram", e.target.value)}/></div>
          </div>
          <div>
            <Label>Segmentos</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {segments.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={()=>toggleSegment(s.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${form.segment_ids.includes(s.id) ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]" : "bg-white text-neutral-700 border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:text-neutral-300 dark:border-white/10"}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-edit-button">{busy ? "Salvando…" : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Support (impersonate with password) Dialog
// ─────────────────────────────────────────────────────────────────────────────
function SupportDialog({ target, onClose, onConfirm }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) setPassword(""); }, [target]);
  if (!target) return null;
  const submit = async () => {
    if (!password) return;
    setBusy(true);
    try { await onConfirm(password); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="support-dialog">
        <DialogHeader><DialogTitle>Prestar suporte</DialogTitle></DialogHeader>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">Para acessar a loja <span className="font-medium">{target.name}</span> como o lojista, insira a senha da loja:</p>
        <Input
          type="password"
          value={password}
          onChange={e=>setPassword(e.target.value)}
          onKeyDown={e=>{ if (e.key === "Enter") submit(); }}
          placeholder="Senha da loja"
          autoFocus
          data-testid="support-password-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !password} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="support-confirm-button">{busy ? "Entrando…" : "Entrar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Login Info Dialog (Enviar login)
// ─────────────────────────────────────────────────────────────────────────────
function LoginInfoDialog({ target, info, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!target || !info) return null;
  const password = info.initial_password || "adminadmin";
  const text = `Link de acesso: gestaoexpert.app.br
Email: ${info.email}
Senha temporária: ${password}
(Recomendamos que ao logar você já troque sua senha temporária)`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(()=>setCopied(false), 1500); toast.success("Copiado"); }
    catch { toast.error("Não foi possível copiar"); }
  };
  const send = () => {
    const url = waLink(target.owner_phone, text);
    if (!url) { toast.error("Lojista sem telefone cadastrado"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <Dialog open={!!target} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="login-info-dialog">
        <DialogHeader><DialogTitle>Enviar login — {target.name}</DialogTitle></DialogHeader>
        <pre className="text-xs bg-neutral-50 dark:bg-white/5 p-3 rounded-md whitespace-pre-wrap font-mono" data-testid="login-info-text">{text}</pre>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="copy-login-button">{copied ? <><Check size={12} className="mr-1"/> Copiado</> : <><Copy size={12} className="mr-1"/> Copiar</>}</Button>
          <Button onClick={send} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-login-wa-button"><MessageCircle size={12} className="mr-1"/> Enviar no WhatsApp</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cancel Subscription Dialog
// ─────────────────────────────────────────────────────────────────────────────
function CancelSubscriptionDialog({ target, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) setNote(""); }, [target]);
  if (!target) return null;
  const submit = async () => {
    if (!window.confirm(`Cancelar definitivamente a assinatura de "${target.name}"?\nO acesso da loja será bloqueado, mas os dados serão mantidos.`)) return;
    setBusy(true);
    try {
      await api.post(`/master/tenants/${target.id}/cancel-subscription`, { note });
      toast.success("Assinatura cancelada");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="cancel-sub-dialog">
        <DialogHeader><DialogTitle>Cancelar assinatura — {target.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">O acesso da loja será bloqueado imediatamente, mas todos os dados (produtos, clientes, vendas, pedidos) serão preservados. Para reativar, basta renovar.</p>
        <div>
          <Label>Observação <span className="text-neutral-400">(opcional)</span></Label>
          <Textarea
            value={note}
            onChange={e=>setNote(e.target.value)}
            rows={3}
            placeholder="Ex: Cliente solicitou cancelamento por inatividade"
            data-testid="cancel-sub-note-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Voltar</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-sub-button">
            <XCircle size={14} className="mr-1"/> {busy ? "Cancelando…" : "Confirmar cancelamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Adesão Dialog — cobrar 1ª mensalidade ou pacote inicial (plano × N meses)
// ─────────────────────────────────────────────────────────────────────────────
function AdesaoDialog({ target, plans, onClose }) {
  // Sempre busca planos frescos ao abrir — evita usar cache stale ou fallback com preços antigos.
  const [freshPlans, setFreshPlans] = useState(null);
  const PLAN_LIST = freshPlans || ((plans && plans.length) ? plans : PLANS);
  const [plan, setPlan] = useState("Start");
  const [months, setMonths] = useState(1);
  const [sendingCheckout, setSendingCheckout] = useState(false);
  useEffect(() => {
    if (target) {
      setPlan(target.plan || "Start");
      setMonths(1);
      // Fetch fresh no-cache para garantir preços atualizados.
      api.get("/master/plans", { _cache: false }).then(r => {
        const list = (r.data || []).map(p => ({ key: p.key, label: p.name || p.key, price: Number(p.price || 0) }));
        if (list.length) setFreshPlans(list);
      }).catch(() => {});
    }
  }, [target]);
  if (!target) return null;
  const planObj = PLAN_LIST.find(p => p.key === plan) || PLAN_LIST[1] || PLAN_LIST[0];
  const total = +(planObj.price * months).toFixed(2);
  const payLink = (() => {
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    const desc = `Adesão ${planObj.label} × ${months} ${months === 1 ? "mês" : "meses"} — ${target.name}`;
    const params = new URLSearchParams({ valor: String(total), desc });
    return `${base}/pagar/master?${params.toString()}`;
  })();
  const sendWa = () => {
    const phone = target.owner_phone;
    if (!phone) { toast.error("Lojista sem telefone cadastrado"); return; }
    const msg = `Olá! Tudo bem?\nPara ativar seu plano finalize o pagamento no link abaixo:\n\n${planObj.label} × ${months} ${months === 1 ? "mês" : "meses"} — ${brl(total)}\n${payLink}`;
    const url = waLink(phone, msg);
    if (!url) { toast.error("Telefone inválido"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const sendStripeCheckout = async (mode /* "card" | "pix" */, copyOnly = false) => {
    const phone = target.owner_phone;
    if (!copyOnly && !phone) { toast.error("Lojista sem telefone cadastrado"); return; }
    // Pre-abre uma janela SÍNCRONA (dentro do click) pra escapar do popup-blocker,
    // depois seta location assim que o link retorna. IMPORTANTE: usar SEM
    // 'noopener' — com 'noopener' o retorno de window.open é null e a mitigação
    // não funciona (o `noopener` é aplicado depois via `waWindow.opener = null`).
    let waWindow = null;
    if (!copyOnly) waWindow = window.open("about:blank", "_blank");
    setSendingCheckout(true);
    try {
      const r = await api.post(`/master/tenants/${target.id}/stripe-checkout`, {
        tenant_id: target.id, plan, months: mode === "card" ? 1 : months, payment_mode: mode,
        origin_url: window.location.origin,
        source: copyOnly ? "admin_direct" : "admin_whatsapp",
      });
      const link = r.data?.checkout_url;
      if (!link) throw new Error("Sem link");
      if (copyOnly) {
        await navigator.clipboard.writeText(link);
        toast.success("Link do checkout copiado para a área de transferência");
        return;
      }
      const modeLabel = mode === "card"
        ? "no cartão (assinatura mensal recorrente)"
        : `via PIX × ${months} ${months === 1 ? "mês" : "meses"}`;
      const msg = `Olá! Segue o link para pagamento/renovação do seu plano do Gestão Expert ${modeLabel}:\n\n${link}\n\nVocê pode realizar o pagamento com toda a segurança pelo Stripe.`;
      const url = waLink(phone, msg);
      if (waWindow && !waWindow.closed) {
        try { waWindow.opener = null; } catch { /* ignore */ }
        waWindow.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      toast.success("Link Stripe gerado e enviado no WhatsApp");
    } catch (e) {
      if (waWindow && !waWindow.closed) waWindow.close();
      toast.error(e?.response?.data?.detail || "Erro ao gerar checkout Stripe. Verifique a configuração.");
    } finally { setSendingCheckout(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="adesao-dialog">
        <DialogHeader><DialogTitle>Cobrar adesão — {target.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500">Selecione o plano e a quantidade de meses para gerar o link de pagamento.</p>
        <div className="space-y-3 mt-3">
          <div>
            <Label>Plano</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="adesao-plan-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {PLAN_LIST.map(p => <SelectItem key={p.key} value={p.key}>{p.label} — {brl(p.price)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Número de meses</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {PAID_PRESETS.map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={()=>setMonths(n)}
                  className={`text-xs px-3 py-1.5 rounded border transition ${months === n ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10"}`}
                  data-testid={`adesao-months-${n}`}
                >
                  {n} {n === 1 ? "mês" : "meses"}
                </button>
              ))}
              <Input
                type="number"
                min="1"
                value={[1,3,6,12,18,24].includes(months) ? "" : months}
                onChange={e => setMonths(Number(e.target.value) || 1)}
                placeholder="Outro…"
                className="h-8 text-sm w-24"
                data-testid="adesao-months-custom"
              />
            </div>
          </div>
        </div>
        <div className="mt-4 p-4 rounded-md bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-900/30">
          <p className="label-tiny text-emerald-700 dark:text-emerald-300">Total a cobrar</p>
          <p className="font-display text-3xl text-emerald-700 dark:text-emerald-300 mt-1" data-testid="adesao-total">{brl(total)}</p>
          <p className="text-[11px] text-neutral-500 mt-1">{brl(planObj.price)} × {months} {months === 1 ? "mês" : "meses"}</p>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            variant="outline"
            onClick={()=>sendStripeCheckout("card", true)}
            disabled={sendingCheckout}
            data-testid="adesao-copy-link-btn"
            title="Copiar link do checkout Stripe (cartão recorrente)"
          ><Copy size={12} className="mr-1"/> Copiar link Checkout</Button>
          <Button onClick={sendWa} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="adesao-send-button"><MessageCircle size={12} className="mr-1"/> WhatsApp (PIX manual)</Button>
          <Button onClick={()=>sendStripeCheckout("pix")} disabled={sendingCheckout} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="adesao-stripe-pix-button"><CreditCard size={12} className="mr-1"/> Checkout PIX (Stripe)</Button>
          <Button onClick={()=>sendStripeCheckout("card")} disabled={sendingCheckout} className="bg-[#635BFF] hover:bg-[#5749f0] text-white" data-testid="adesao-stripe-card-button"><CreditCard size={12} className="mr-1"/> Cartão recorrente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin Settings (Painel do gestor) Dialog
// ─────────────────────────────────────────────────────────────────────────────
function AdminSettingsDialog({ open, onClose, initial, metrics, onSaved }) {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  const [stripeOpen, setStripeOpen] = useState(false);
  const [availPlans, setAvailPlans] = useState([]);
  useEffect(() => {
    if (open) {
      setForm(initial || {});
      api.get("/master/plans").then(r => setAvailPlans(r.data || [])).catch(() => setAvailPlans([]));
    }
  }, [open, initial]);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.put("/master/settings", form);
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="admin-settings-dialog">
        <DialogHeader><DialogTitle>Painel do gestor</DialogTitle></DialogHeader>
        <Card className="p-4 border-emerald-200 dark:border-emerald-900/30 bg-emerald-50/50 dark:bg-emerald-900/10 shadow-none">
          <p className="label-tiny text-emerald-700 dark:text-emerald-300">Faturamento mensal estimado</p>
          <p className="font-display text-3xl text-emerald-700 dark:text-emerald-300 mt-1"><Money value={metrics.monthly_revenue_estimate}/></p>
          <p className="text-[11px] text-neutral-500 mt-1">{metrics.active_paid_tenants} assinante(s) ativo(s) · {metrics.active_trial_tenants} em teste · {metrics.disabled_tenants} desativado(s)</p>
        </Card>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Meu WhatsApp (suporte)</Label>
            <Input value={form.whatsapp || ""} onChange={e=>upd("whatsapp", e.target.value)} placeholder="+5511999999999" data-testid="admin-whatsapp-input"/>
            <p className="text-[11px] text-neutral-500 mt-1">Mostrado para clientes com plano expirado.</p>
          </div>
          <div className="pt-2 border-t border-[#E5E5E5] dark:border-white/10">
            <p className="label-tiny mb-3">Dados PIX (para gerar copia-e-cola)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo da chave</Label>
                <Select value={form.pix_type || ""} onValueChange={(v)=>upd("pix_type", v)}>
                  <SelectTrigger data-testid="admin-pix-type-select"><SelectValue placeholder="Tipo"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CPF">CPF</SelectItem>
                    <SelectItem value="CNPJ">CNPJ</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Celular">Celular</SelectItem>
                    <SelectItem value="Aleatória">Aleatória</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cidade</Label>
                <Input value={form.pix_city || ""} onChange={e=>upd("pix_city", e.target.value)} placeholder="SAO PAULO" data-testid="admin-pix-city-input"/>
              </div>
            </div>
            <div className="mt-3">
              <Label>Chave PIX</Label>
              <Input value={form.pix_key || ""} onChange={e=>upd("pix_key", e.target.value)} placeholder="email@pix, CPF ou aleatória" data-testid="admin-pix-key-input"/>
            </div>
            <div className="mt-3">
              <Label>Nome do titular</Label>
              <Input value={form.pix_holder || ""} onChange={e=>upd("pix_holder", e.target.value)} placeholder="Nome completo" data-testid="admin-pix-holder-input"/>
            </div>
          </div>
          <div className="pt-3 border-t border-[#E5E5E5] dark:border-white/10">
            <p className="label-tiny mb-2">Checkout — Planos exibidos na página /cadastro</p>
            <p className="text-[11px] text-neutral-500 mb-2">Selecione os planos que serão apresentados aos novos assinantes na página pública. Se nenhum for selecionado, todos os planos ativos aparecem.</p>
            <div className="grid grid-cols-2 gap-2">
              {availPlans.map(p => {
                const selected = ((form.checkout_plans || []).includes(p.key));
                return (
                  <button
                    key={p.key} type="button"
                    onClick={()=>{
                      const cur = form.checkout_plans || [];
                      const nxt = selected ? cur.filter(k => k !== p.key) : [...cur, p.key];
                      upd("checkout_plans", nxt);
                    }}
                    className={`text-left p-2 rounded-md border text-xs ${selected ? "border-[#0A0A0A] bg-neutral-50 dark:bg-white/10 dark:border-white/30" : "border-[#E5E5E5] dark:border-white/10"}`}
                    data-testid={`checkout-plan-${p.key}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`w-3 h-3 inline-flex items-center justify-center rounded border ${selected ? "bg-emerald-500 border-emerald-500 text-white" : "border-neutral-300"}`}>{selected && <Check size={9}/>}</span>
                      <span className="font-medium">{p.label || p.key}</span>
                    </div>
                    <div className="mt-1 text-neutral-500">R$ {Number(p.price || 0).toFixed(2).replace(".",",")}</div>
                  </button>
                );
              })}
              {availPlans.length === 0 && <div className="text-xs text-neutral-500 col-span-2">Nenhum plano cadastrado.</div>}
            </div>
          </div>
          <div className="pt-3 border-t border-[#E5E5E5] dark:border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-tiny">Pagamentos → Stripe</p>
                <p className="text-[11px] text-neutral-500 mt-0.5">Cobrança automática por cartão recorrente e PIX avulso.</p>
              </div>
              <Button size="sm" variant="outline" type="button" onClick={()=>setStripeOpen(true)} data-testid="open-stripe-config">
                <CreditCard size={12} className="mr-1"/> Configurar Stripe
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-admin-settings-button">{busy ? "Salvando…" : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
      <Dialog open={stripeOpen} onOpenChange={(o)=>!o && setStripeOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="stripe-config-dialog">
          <DialogHeader>
            <DialogTitle>Configurar Stripe</DialogTitle>
            <DialogDescription>Chaves de API, ambiente e URL do webhook para receber os eventos de pagamento.</DialogDescription>
          </DialogHeader>
          <StripeConfigPanel onClose={()=>setStripeOpen(false)}/>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
